import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check } from 'lucide-react';
import { Button } from './ui/button';
import { requestNotificationPermission } from '../config/firebase';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const NotificationSetup = () => {
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Check current permission status on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPermissionStatus(Notification.permission);
      
      // Check if user already has a token saved
      const savedToken = localStorage.getItem('fcm_token');
      if (savedToken && Notification.permission === 'granted') {
        setIsSubscribed(true);
      }
    }
  }, []);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    
    try {
      const token = await requestNotificationPermission();
      
      if (token) {
        await api.saveFcmToken(token);
        localStorage.setItem('fcm_token', token);
        setIsSubscribed(true);
        setPermissionStatus('granted');
        
        toast({
          title: "Notifications Enabled",
          description: "You'll receive alerts for new bookings, cancellations, and payments.",
        });
      } else {
        toast({
          title: "Permission Denied",
          description: "Please enable notifications in your browser settings.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast({
        title: "Error",
        description: "Failed to enable notifications. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('fcm_token');
      if (token) {
        await api.removeFcmToken(token);
        localStorage.removeItem('fcm_token');
      }
      
      setIsSubscribed(false);
      
      toast({
        title: "Notifications Disabled",
        description: "You won't receive push notifications anymore.",
      });
    } catch (error) {
      console.error('Error disabling notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if notifications aren't supported
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return null;
  }

  // Permission was denied - show how to enable
  if (permissionStatus === 'denied') {
    return (
      <button 
        onClick={() => {
          toast({
            title: "Enable in Browser Settings",
            description: "Click the lock icon in the address bar → Site settings → Notifications → Allow",
            duration: 8000,
          });
        }}
        className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm w-full text-left hover:bg-red-100 transition-colors"
      >
        <BellOff className="h-4 w-4 text-red-500 flex-shrink-0" />
        <span className="text-red-700 text-xs">Notifications blocked. Enable in browser settings.</span>
      </button>
    );
  }

  // Already subscribed - show enabled state
  if (isSubscribed) {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 text-green-600" />
          <span className="text-green-700 text-xs">Notifications on</span>
        </div>
        <button 
          onClick={handleDisableNotifications}
          disabled={isLoading}
          className="p-1 hover:bg-green-100 rounded transition-colors"
          title="Disable notifications"
        >
          <BellOff className="h-3 w-3 text-green-600" />
        </button>
      </div>
    );
  }

  // Not subscribed - show enable button
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleEnableNotifications}
      disabled={isLoading}
      className="w-full gap-2 text-xs"
    >
      <Bell className="h-4 w-4" />
      {isLoading ? 'Enabling...' : 'Enable Notifications'}
    </Button>
  );
};

export default NotificationSetup;
